<template>
  <selectable-table-modal
    v-model:lists-selected="configurationsToRemoveSelected"
    class="remove-configurations-from-list-modal"
    :show-modal="showModal"
    :columns="table.columns"
    :rows="list?.configurationIds?.length ? table.rows : []"
    :table-loading="areConfigurationsFromListLoading"
    header-title="REMOVE CONFIGURATIONS"
    :on-handle-confirm="openRemoveConfirmationDialog"
    :confirm-btn-text="`Delete ${ configurationsToRemoveSelected.length } item(s)`"
    items-type="Configurations"
    :custom-header="true"
    :disable-confirm="configurationsToRemoveSelected.length === 0"
    no-data-label="No configurations found."
    loading-label="Loading list's configurations. Please wait ..."
    @update:show-modal="closeModal"
  >
  </selectable-table-modal>

  <!-- Remove Configurations Confirmation Dialog -->
  <mi-dialog
    :model-value="showRemoveConfirmationDialog"
    class="show-delete-confirmation-dialog"
    no-esc-dismiss
    no-backdrop-dismiss
    :close-icon="false"
  >
    <h3
      class="q-mt-none q-mb-sm modal-confirmation-delete-header"
    >
      Are you sure you want to remove these configurations?
    </h3>

    <p
      class="modal-confirmation-delete"
    >
      This action cannot be undone.
    </p>

    <template #actions>
      <mi-btn flat @click="showRemoveConfirmationDialog = false"> Cancel  </mi-btn>
      <mi-btn color="accent" :loading="loadingDelete" @click="onHandleConfirm"> Confirm </mi-btn>
    </template>
  </mi-dialog>
</template>

<script>
  import { onMounted, ref, watch } from 'vue'
  import { getConfigurations, updateConfigurationsList } from '@/api'
  import { useTable } from '@/composables/useTable'
  import {
    REMOVE_CONFIGS_FROM_SAVED_LISTS_TABLE_COLUMNS
  } from '@/components/pac/saved-lists/utils'
  import SelectableTableModal from '@/components/pac/saved-lists/SelectableTableModal.vue'
  import MiDialog from '@/packages/@mi-library/MiDialog/MiDialog.vue'
  import MiBtn from '@/packages/@mi-library/MiBtn/MiBtn.vue'

  export default {
    name: 'RemoveConfigurationsFromListModal',
    components: { MiBtn, MiDialog, SelectableTableModal },
    props: {
      showModal: {
        type: Boolean,
        default: false
      },
      selectedConfigurationsIdsSet: {
        type: Object,
        required: false,
        default: () => {}
      },
      list: {
        type: Object,
        required: true,
        default: () => {}
      },
      refreshSavedListsTable: {
        type: Function,
        required: true,
        default: () => ({})
      }
    },
    emits: ['update:show-modal'],
    setup(props, { emit }) {
      const configurationsToRemoveSelected = ref([])
      const showRemoveConfirmationDialog = ref(false)
      const loadingDelete = ref(false)
      const { table, callbacks: tableCallbacks } = useTable(REMOVE_CONFIGS_FROM_SAVED_LISTS_TABLE_COLUMNS)
      const areConfigurationsFromListLoading = ref(false)

      const closeModal = () => {
        emit('update:show-modal')
      }

      const deselectAllConfigurations = () => {
        configurationsToRemoveSelected.value = []
      }

      const openRemoveConfirmationDialog = selectedConfigurationIds => {
        showRemoveConfirmationDialog.value = true
        configurationsToRemoveSelected.value = selectedConfigurationIds
      }

      const fetchConfigurationsFromList = async () => {
        if (props.list?.configurationIds.length) {
          areConfigurationsFromListLoading.value = true
          const fetchExistingListsPromise = () => getConfigurations({
            page: 0,
            size: 1000,
            sortBy: 'modifiedDate',
            sortDirection: 'DESC',
            ids: props.list.configurationIds
          })
          await tableCallbacks.fetchTableData(fetchExistingListsPromise)
          areConfigurationsFromListLoading.value = false
        }
      }

      const onHandleConfirm = async () => {
        try {
          loadingDelete.value = true

          const updatedConfigurationIds = table.rows
            .map(row => row.id)
            .filter(id => !configurationsToRemoveSelected.value.includes(id))

          await updateConfigurationsList({
            listId: props.list.id,
            configurationIds: updatedConfigurationIds
          })

          showRemoveConfirmationDialog.value = false
          closeModal()
          props.refreshSavedListsTable()
          deselectAllConfigurations()
        }
        catch (err) {
          console.error(err)
        }
        finally {
          loadingDelete.value = false
        }
      }

      watch(() => props.list.id, async () => {
        await fetchConfigurationsFromList()
      })

      onMounted(async () => {
        await fetchConfigurationsFromList()
      })

      return {
        onHandleConfirm,
        configurationsToRemoveSelected,
        loadingDelete,
        closeModal,
        areConfigurationsFromListLoading,
        table,
        openRemoveConfirmationDialog,
        showRemoveConfirmationDialog
      }
    }
  }
</script>

<style lang="scss" scoped>
  //stylelint-disable declaration-no-important
  .remove-configurations-from-list-modal ::v-deep(.q-table thead) {
    display: none !important;
  }

  .show-delete-confirmation-dialog {
    .q-dialog__inner > .mi-card {
      width: 592px;
      max-width: 769px;
    }
  }
</style>
